// The one component every ad preview wraps its media in.
//
// Takes the creative's canvas shape and the chrome to show, and hands
// back a correctly-proportioned media slot with the platform furniture
// drawn over or around it. Callers keep rendering their own <video> /
// <Image> as children — the frame only owns the box those sit in, so
// poster frames, cache-busting, controls and click handlers all stay
// where they were.
//
// Two densities, which differ in how they get their size:
//
//   full    — detail views and modals. Sized by WIDTH, capped to a
//             phone-ish measure and centred, the way Ads Manager shows
//             its own previews; height flows from the content. Needs
//             no height from the parent.
//   compact — grid tiles. Fits a fixed-height parent. Overlay
//             placements letterbox inside it. Feed placements lay out
//             at natural proportions and are then scaled down as a
//             whole, so the tile shows a true miniature of the card
//             rather than a card with a squashed, letterboxed media
//             slot — feed media is full-bleed, and it has to still look
//             full-bleed at thumbnail size.

import type { CSSProperties, ReactNode } from 'react';
import { ReelsChrome, StoriesChrome } from './overlays';
import { IgFeedCard, FbFeedCard } from './cards';
import { YouTubeShortsChrome, YouTubeInstreamChrome } from './googleOverlays';
import { DiscoverCard, DisplayCard, GmailCard, YouTubeInFeedCard } from './googleCards';
import { SafeZoneGuardrail } from './SafeZoneGuardrail';
import type { SafeAreaPct } from './SafeZoneGuardrail';
import { useElementSize } from './useElementSize';
import { chromePlacementModel, ratioDimensions } from './types';
import type { AdRatio, ChromeDensity, ChromeIdentity, ChromeKind } from './types';

// Rendering a 9:16 Reels preview at the full width of a 3xl modal makes
// a two-storey phone. Real placement previews are shown at handset
// measure; these caps keep the chrome legible and honest about scale.
const DEFAULT_MAX_WIDTH: Record<'overlay' | 'card', number> = {
  overlay: 380,
  card:    500
};

// …and a 9:16 creative at 380px wide is still 675px tall, which
// overflows a modal on a laptop. Cap the media's HEIGHT too and derive
// the width from it, so a vertical preview stays on screen while a
// square one still gets the full measure. Feed cards get the tighter
// budget because their furniture adds height above and below.
const MAX_MEDIA_HEIGHT: Record<'overlay' | 'card', number> = {
  overlay: 560,
  card:    420
};

function fullDensityMaxWidth(
  model: 'overlay' | 'card',
  ratio: { w: number; h: number },
  override?: number
): number {
  if (override != null) return override;
  const byHeight = MAX_MEDIA_HEIGHT[model] * (ratio.w / ratio.h);
  return Math.round(Math.min(DEFAULT_MAX_WIDTH[model], byHeight));
}

// Card-model chrome → its component. Keys must stay in step with
// CARD_CHROMES in types.ts; see the lookup site below for why a missing
// entry degrades to a bare creative instead of a default card.
type CardComponent = (props: {
  identity:        ChromeIdentity;
  u:               number;
  density:         ChromeDensity;
  showSponsored?:  boolean;
  showEngagement?: boolean;
  children:        ReactNode;
}) => JSX.Element;

const CARD_COMPONENTS: Partial<Record<ChromeKind, CardComponent>> = {
  'ig-feed':     IgFeedCard,
  'fb-feed':     FbFeedCard,
  'discover':    DiscoverCard,
  'gdn-display': DisplayCard,
  'gmail':       GmailCard,
  'yt-infeed':   YouTubeInFeedCard
};

export type AdChromeFrameProps = {
  chrome:   ChromeKind;
  ratio:    AdRatio;
  identity: ChromeIdentity;
  density?: ChromeDensity;
  // Platform-disclosure row ("Sponsored" / Google "Ad" chip). Defaults
  // on so existing callers stay pixel-identical.
  showSponsored?: boolean;
  // Like/comment/send rail plus generic like-count and caption body.
  // Defaults on so existing callers stay pixel-identical.
  showEngagement?: boolean;
  // Ads-Manager clear zones over the creative. Title Studio exposes
  // this as its own toggle; the ad pages don't.
  showSafeZones?: boolean;
  // Lets a caller show the guardrail for a placement other than the
  // selected chrome — Title Studio paints the strictest vertical bands
  // over a bare creative so the toggle stays meaningful with chrome off.
  safeZoneKind?: ChromeKind;
  // The placement's REAL reserved bands, as fractions of frame height, from
  // GET /api/ads/formats `safeAreaPct`. When supplied the guardrail's
  // top/bottom bands are drawn from the clamp the renderer actually applies
  // instead of this module's eyeballed approximations. Omit it and the
  // approximations stand.
  safeArea?: SafeAreaPct | null;
  // Overrides the phone-measure cap in `full` density. Ignored when
  // compact, where the parent tile dictates size.
  maxWidth?: number;
  // Backdrop behind letterboxed media.
  background?: string;
  children: ReactNode;
};

export function AdChromeFrame({
  chrome,
  ratio,
  identity,
  density = 'full',
  showSponsored = true,
  showEngagement = true,
  showSafeZones = false,
  safeZoneKind,
  safeArea,
  maxWidth,
  background = '#000',
  children
}: AdChromeFrameProps) {
  const [outerRef, outer] = useElementSize<HTMLDivElement>();
  const [cardRef, card] = useElementSize<HTMLDivElement>();
  const { w, h } = ratioDimensions(ratio);
  const model = chromePlacementModel(chrome);
  const guardrailKind = safeZoneKind ?? chrome;
  const compact = density === 'compact';

  const mediaAspect: CSSProperties = {
    position: 'relative',
    aspectRatio: `${w} / ${h}`,
    background,
    overflow: 'hidden'
  };

  // ── Overlay placements (and bare creative) ─────────────────────────
  if (model === 'overlay') {
    const Overlay =
      chrome === 'reels'       ? ReelsChrome           :
      chrome === 'stories'     ? StoriesChrome         :
      chrome === 'yt-shorts'   ? YouTubeShortsChrome   :
      chrome === 'yt-instream' ? YouTubeInstreamChrome : null;

    const u = outer.width / 100;
    const boxStyle: CSSProperties = compact
      ? { ...mediaAspect, height: '100%', width: 'auto', maxWidth: '100%' }
      : { ...mediaAspect, width: '100%', maxWidth: fullDensityMaxWidth('overlay', { w, h }, maxWidth) };

    return (
      <Centered fill={compact}>
        <div ref={outerRef} style={boxStyle}>
          {children}
          {Overlay && outer.width > 0 && (
            <Overlay
              identity={identity}
              u={u}
              density={density}
              showSponsored={showSponsored}
              showEngagement={showEngagement}
            />
          )}
          {showSafeZones && <SafeZoneGuardrail kind={guardrailKind} safeArea={safeArea} />}
        </div>
      </Centered>
    );
  }

  // ── Feed / unit cards ──────────────────────────────────────────────
  //
  // A lookup rather than a ternary chain with a trailing default: the
  // default arm would have to name SOME card, and a card kind added to
  // CARD_CHROMES (types.ts) but forgotten here would then silently
  // render another platform's furniture — a Google ad in a Facebook
  // card, which is the exact defect this module exists to prevent.
  // Missing entry → undefined → bare creative, which is visibly wrong
  // rather than plausibly wrong.
  const Card = CARD_COMPONENTS[chrome];

  if (!Card) {
    return (
      <Centered fill={compact}>
        <div style={{ ...mediaAspect, width: '100%' }}>{children}</div>
      </Centered>
    );
  }

  if (!compact) {
    // Width-driven: the card flows to whatever height it needs.
    const shellWidth = fullDensityMaxWidth('card', { w, h }, maxWidth);
    const u = outer.width / 100;
    return (
      <Centered fill={false}>
        <div ref={outerRef} style={{ width: '100%', maxWidth: shellWidth }}>
          {outer.width > 0 ? (
            <Card
              identity={identity}
              u={u}
              density={density}
              showSponsored={showSponsored}
              showEngagement={showEngagement}
            >
              <div style={{ ...mediaAspect, width: '100%' }}>
                {children}
                {showSafeZones && <SafeZoneGuardrail kind={guardrailKind} safeArea={safeArea} />}
              </div>
            </Card>
          ) : (
            <div style={{ ...mediaAspect, width: '100%' }}>{children}</div>
          )}
        </div>
      </Centered>
    );
  }

  // Compact: lay the card out at natural proportions against the tile's
  // width, measure it, then scale the whole thing down to fit the
  // tile's height. Scaling (rather than shrinking the media slot) is
  // what keeps the creative full-bleed in the card at thumbnail size.
  const u = outer.width / 100;
  const scale = card.height > 0 && outer.height > 0
    ? Math.min(1, outer.height / card.height)
    : 1;

  return (
    <div
      ref={outerRef}
      style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', minWidth: 0, minHeight: 0
      }}
    >
      <div style={{
        width: '100%',
        transform: `scale(${scale})`,
        transformOrigin: 'center center',
        // Until the first measurement lands the card is unscaled; hide
        // it for that frame rather than flashing an oversized card.
        visibility: card.height > 0 ? 'visible' : 'hidden'
      }}>
        <div ref={cardRef}>
          {outer.width > 0 && (
            <Card
              identity={identity}
              u={u}
              density={density}
              showSponsored={showSponsored}
              showEngagement={showEngagement}
            >
              <div style={{ ...mediaAspect, width: '100%' }}>
                {children}
                {showSafeZones && <SafeZoneGuardrail kind={guardrailKind} safeArea={safeArea} />}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Centered({ children, fill }: { children: ReactNode; fill: boolean }) {
  return (
    <div style={{
      width: '100%',
      height: fill ? '100%' : undefined,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: 0, minWidth: 0
    }}>
      {children}
    </div>
  );
}
